// Localização: frontend/src/components/Financeiro/ListaContasReceber.jsx
// Lista de contas a receber

import React, { useState, useCallback } from 'react';
import LoadingIndicator from '../Common/LoadingIndicator';
import ResumoFinanceiro from './ResumoFinanceiro';
import PrimaryButton from '../Common/PrimaryButton';
import ModalRecebimentoLote from './ModalRecebimentoLote';
import { toast } from 'react-toastify';
import {
    DollarSign,
    Calendar,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    SquarePen,
    Trash2,
    CheckSquare,
    Square
} from 'lucide-react';

const ListaContasReceber = ({
    contas,
    isLoading,
    onEdit,
    onDelete,
    onReceber,
    pagination,
    onPageChange,
    canEdit,
    canDelete,
    receber, // Função para receber diretamente (para lote)
    receberParcial, // Função para receber parcialmente (para lote)
    onRefresh // Callback para recarregar lista após recebimento em lote
}) => {
    // Valores padrão: se não for passado, assume true (permissão concedida)
    const hasEditPermission = canEdit !== false;
    const hasDeletePermission = canDelete !== false;
    
    // Estado para seleção múltipla
    const [selectedContas, setSelectedContas] = useState(new Set());
    const [isModalLoteOpen, setIsModalLoteOpen] = useState(false);
    
    // Função segura para editar
    const handleEdit = useCallback((conta) => {
        if (onEdit && typeof onEdit === 'function') {
            onEdit(conta);
        }
    }, [onEdit]);
    
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'Pendente': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pendente' },
            'Recebido': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Recebido' },
            'Vencido': { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle, label: 'Atrasado' },
            'Cancelado': { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle, label: 'Cancelado' }
        };

        const config = statusConfig[status] || statusConfig['Pendente'];
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                <Icon className="h-3 w-3 mr-1" />
                {config.label}
            </span>
        );
    };

    // Funções para seleção múltipla
    const toggleSelectConta = useCallback((contaId) => {
        setSelectedContas(prev => {
            const newSet = new Set(prev);
            if (newSet.has(contaId)) {
                newSet.delete(contaId);
            } else {
                newSet.add(contaId);
            }
            return newSet;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        const contasRecebiveis = contas.filter(c => 
            (c.status === 'Pendente' || c.status === 'Vencido') && parseFloat(c.valor_pendente) > 0
        );
        
        if (selectedContas.size === contasRecebiveis.length) {
            setSelectedContas(new Set());
        } else {
            setSelectedContas(new Set(contasRecebiveis.map(c => c.id)));
        }
    }, [contas, selectedContas.size]);

    const handleReceberLote = useCallback(async (contasSelecionadas, data) => {
        // Se tiver função receber direta, usar ela (para lote)
        if (receber) {
            const resultados = [];
            const erros = [];
            
            // Processar cada conta individualmente
            for (const conta of contasSelecionadas) {
                try {
                    await receber(conta.id, data);
                    resultados.push(conta.id);
                } catch (error) {
                    console.error(`Erro ao receber conta ${conta.id}:`, error);
                    erros.push({ id: conta.id, descricao: conta.descricao, erro: error.message || 'Erro desconhecido' });
                }
            }
            
            // Limpar seleção após processar
            setSelectedContas(new Set());
            
            // Mostrar resumo do processamento
            if (resultados.length > 0 && erros.length === 0) {
                toast.success(`${resultados.length} ${resultados.length === 1 ? 'conta recebida' : 'contas recebidas'} com sucesso!`);
            } else if (resultados.length > 0 && erros.length > 0) {
                toast.warning(`${resultados.length} ${resultados.length === 1 ? 'conta recebida' : 'contas recebidas'} com sucesso, mas ${erros.length} ${erros.length === 1 ? 'falhou' : 'falharam'}.`);
            } else if (erros.length > 0) {
                toast.error(`Erro ao receber ${erros.length} ${erros.length === 1 ? 'conta' : 'contas'}.`);
            }
            
            // Recarregar lista se houver callback
            if (onRefresh) {
                onRefresh();
            }
            
            // Retornar resultados para o componente pai poder mostrar feedback
            return { resultados, erros };
        } else {
            // Fallback: usar onReceber (abre modal individual)
            // Não ideal para lote, mas mantém compatibilidade
            console.warn('Recebimento em lote não disponível - função receber não fornecida');
            setSelectedContas(new Set());
            return { resultados: [], erros: [] };
        }
    }, [receber, onRefresh]);

    const contasSelecionadas = contas.filter(c => selectedContas.has(c.id));
    const contasRecebiveis = contas.filter(c => 
        (c.status === 'Pendente' || c.status === 'Vencido') && parseFloat(c.valor_pendente) > 0
    );
    const todasSelecionadas = contasRecebiveis.length > 0 && 
        contasRecebiveis.every(c => selectedContas.has(c.id));

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <LoadingIndicator />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Barra de Ações com Seleção Múltipla */}
            {contasRecebiveis.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={toggleSelectAll}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                            title={todasSelecionadas ? 'Desmarcar todas' : 'Selecionar todas'}
                        >
                            {todasSelecionadas ? (
                                <CheckSquare className="h-5 w-5 text-emerald-600" />
                            ) : (
                                <Square className="h-5 w-5 text-gray-400" />
                            )}
                            <span>{todasSelecionadas ? 'Desmarcar Todas' : 'Selecionar Todas'}</span>
                        </button>
                        {selectedContas.size > 0 && (
                            <span className="text-sm text-gray-600">
                                {selectedContas.size} {selectedContas.size === 1 ? 'conta selecionada' : 'contas selecionadas'}
                            </span>
                        )}
                    </div>
                    {selectedContas.size > 0 && (
                        <PrimaryButton
                            onClick={() => setIsModalLoteOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <DollarSign className="h-4 w-4" />
                            Receber {selectedContas.size} {selectedContas.size === 1 ? 'Conta' : 'Contas'}
                        </PrimaryButton>
                    )}
                </div>
            )}

            {/* Resumo Estatístico */}
            <ResumoFinanceiro contas={contas} tipo="receber" />

            {/* Tabela de Contas (Desktop) */}
            {!contas || contas.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">Nenhuma conta a receber encontrada</p>
                    <p className="text-gray-400 text-sm mt-2">Crie uma nova conta para começar</p>
                </div>
            ) : (
                <>
                    {/* Desktop View */}
                    <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                                            <button
                                                onClick={toggleSelectAll}
                                                className="flex items-center"
                                                title={todasSelecionadas ? 'Desmarcar todas' : 'Selecionar todas'}
                                            >
                                                {todasSelecionadas ? (
                                                    <CheckSquare className="h-5 w-5 text-emerald-600" />
                                                ) : (
                                                    <Square className="h-5 w-5 text-gray-400" />
                                                )}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Descrição
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Cliente
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Categoria
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Vencimento
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Valor Total
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Valor Pendente
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {contas.map((conta) => {
                                        const isRecebivel = (conta.status === 'Pendente' || conta.status === 'Vencido') && 
                                                           parseFloat(conta.valor_pendente) > 0;
                                        const isSelected = selectedContas.has(conta.id);
                                        
                                        return (
                                        <tr key={conta.id} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-emerald-50' : ''}`}>
                                            <td className="px-6 py-4">
                                                {isRecebivel && (
                                                    <button
                                                        onClick={() => toggleSelectConta(conta.id)}
                                                        className="flex items-center"
                                                        title={isSelected ? 'Desmarcar' : 'Selecionar'}
                                                    >
                                                        {isSelected ? (
                                                            <CheckSquare className="h-5 w-5 text-emerald-600" />
                                                        ) : (
                                                            <Square className="h-5 w-5 text-gray-400 hover:text-emerald-600" />
                                                        )}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {conta.descricao}
                                                </div>
                                                {conta.observacoes && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {conta.observacoes.substring(0, 50)}
                                                        {conta.observacoes.length > 50 ? '...' : ''}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {conta.cliente_fornecedor || '-'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {conta.categoria?.nome || '-'}
                                                </div>
                                                {conta.subcategoria && (
                                                    <div className="text-xs text-gray-500">
                                                        {conta.subcategoria.nome}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-1 text-sm text-gray-900">
                                                    <Calendar className="h-3 w-3 text-gray-400" />
                                                    {formatDate(conta.data_vencimento)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                                                {formatCurrency(conta.valor_total)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-orange-600">
                                                {formatCurrency(conta.valor_pendente)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(conta.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-2">
                                                    {(conta.status === 'Pendente' || conta.status === 'Vencido') && (
                                                        <button
                                                            onClick={() => onReceber(conta)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-xs font-medium"
                                                            title="Receber"
                                                        >
                                                            <DollarSign className="h-3 w-3" />
                                                            Receber
                                                        </button>
                                                    )}

                                                    {conta.status !== 'Recebido' && hasEditPermission && onEdit && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                if (onEdit && typeof onEdit === 'function') {
                                                                    onEdit(conta);
                                                                }
                                                            }}
                                                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                                            title="Editar"
                                                        >
                                                            <SquarePen className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {conta.status !== 'Recebido' && hasDeletePermission && (
                                                        <button
                                                            onClick={() => onDelete(conta.id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title={conta.status === 'Cancelado' ? 'Confirmar Cancelamento' : 'Cancelar'}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile View */}
                    <div className="md:hidden space-y-4">
                        {contas.map((conta) => {
                            const isRecebivel = (conta.status === 'Pendente' || conta.status === 'Vencido') && 
                                               parseFloat(conta.valor_pendente) > 0;
                            const isSelected = selectedContas.has(conta.id);
                            
                            return (
                            <div key={conta.id} className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${isSelected ? 'ring-2 ring-emerald-500' : ''}`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-start gap-2 flex-1">
                                        {isRecebivel && (
                                            <button
                                                onClick={() => toggleSelectConta(conta.id)}
                                                className="mt-1"
                                                title={isSelected ? 'Desmarcar' : 'Selecionar'}
                                            >
                                                {isSelected ? (
                                                    <CheckSquare className="h-5 w-5 text-emerald-600" />
                                                ) : (
                                                    <Square className="h-5 w-5 text-gray-400" />
                                                )}
                                            </button>
                                        )}
                                        <div className="flex-1">
                                            <h3 className="text-sm font-medium text-gray-900">{conta.descricao}</h3>
                                            <p className="text-xs text-gray-500">{conta.cliente_fornecedor}</p>
                                        </div>
                                    </div>
                                    {getStatusBadge(conta.status)}
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                                    <div>
                                        <p className="text-gray-500 text-xs">Vencimento</p>
                                        <div className="flex items-center gap-1 font-medium text-gray-900">
                                            <Calendar className="h-3 w-3 text-gray-400" />
                                            {formatDate(conta.data_vencimento)}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs">Categoria</p>
                                        <p className="font-medium text-gray-900">{conta.categoria?.nome || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs">Valor Total</p>
                                        <p className="font-semibold text-emerald-600">{formatCurrency(conta.valor_total)}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs">Pendente</p>
                                        <p className="font-semibold text-orange-600">{formatCurrency(conta.valor_pendente)}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                                    {(conta.status === 'Pendente' || conta.status === 'Vencido') && (
                                        <button
                                            onClick={() => onReceber(conta)}
                                            className="flex-1 inline-flex justify-center items-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                                        >
                                            <DollarSign className="h-4 w-4" />
                                            Receber
                                        </button>
                                    )}

                                    <div className="flex gap-2">
                                        {conta.status !== 'Recebido' && hasEditPermission && onEdit && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (onEdit && typeof onEdit === 'function') {
                                                        onEdit(conta);
                                                    }
                                                }}
                                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-100"
                                                title="Editar"
                                            >
                                                <SquarePen className="w-4 h-4" />
                                            </button>
                                        )}
                                        {conta.status !== 'Recebido' && hasDeletePermission && (
                                            <button
                                                onClick={() => onDelete(conta.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100"
                                                title={conta.status === 'Cancelado' ? 'Confirmar Cancelamento' : 'Cancelar'}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                        })}
                    </div>
                </>
            )}

            {/* Paginação */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-600">
                        Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                        {pagination.total} contas
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => onPageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Anterior
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                            Página {pagination.page} de {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => onPageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.totalPages}
                            className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Próxima
                        </button>
                    </div>
                </div>
            )}

            {/* Modal de Recebimento em Lote */}
            <ModalRecebimentoLote
                isOpen={isModalLoteOpen}
                onClose={() => {
                    setIsModalLoteOpen(false);
                }}
                contas={contasSelecionadas}
                onReceber={handleReceberLote}
            />
        </div>
    );
};

export default ListaContasReceber;
